import { FilterValue, FilterValueType } from '../types';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';

/**
 * Filter Operator for comparing field value is equal to the filter value
 */
export function equal<T>(row: T, fields: (keyof T)[], filterValueType: FilterValueType, filterValue: FilterValue, fieldProperty?: string) {
	const isEqual: boolean = fields.reduce((fieldIsEqual, field) => {
		// Since we are always using OR logic, once we find a single match the rest doesn't matter just continue
		if (fieldIsEqual) {
			return true;
		}

		let fieldValue: unknown = row[field];
		if (fieldValue && typeof fieldValue === 'object' && fieldProperty) {
			const obj = fieldValue as Record<string, unknown>;
			fieldValue = obj[fieldProperty];
		}

		/**
		 * A blank filter carries no value at all — it is asking for the rows the field holds nothing on, which is how a
		 * query finds the legs with no driver assigned. The API is inconsistent about which flavour of nothing a missing
		 * field arrives as, so all three count on both sides.
		 */
		if (filterValue === undefined || filterValue === null) {
			return fieldValue === undefined || fieldValue === null || fieldValue === '';
		}

		// Do specific date logic if applicable. We should already have it stored as unix from the API transform.
		if (filterValueType === 'date' && typeof fieldValue === 'number' && typeof filterValue === 'number') {
			// The filter Value for a date should contain the amount of days to add/subtract to today. So today = 0, tomorrow = 1, yesterday = -1, etc.
			const now = dayjsNow().add(filterValue, 'd');
			const bod = now.startOf('d').valueOf();
			const eod = now.endOf('d').valueOf();

			return bod <= fieldValue && fieldValue <= eod;
		}

		switch (typeof fieldValue) {
			case 'string':
				return (
					fieldValue.localeCompare(String(filterValue), 'en', {
						sensitivity: 'base'
					}) === 0
				);

			case 'number':
				return fieldValue === (filterValue ? Number(filterValue) : filterValue);

			case 'boolean':
				return fieldValue === (filterValue ? Boolean(filterValue) : filterValue);

			default:
				return fieldValue === filterValue;
		}
	}, false);

	return isEqual;
}

export function notEqual<T>(
	row: T,
	fields: (keyof T)[],
	filterValueType: FilterValueType,
	filterValue: FilterValue,
	fieldProperty?: string
) {
	return !equal(row, fields, filterValueType, filterValue, fieldProperty);
}
